import { Controller } from "@hotwired/stimulus"
import {
  findWalletByAddress,
  buildTransferTransaction,
  signAndSend,
  detectChain,
  explorerUrl,
  getCsrfToken,
} from "@solrengine/wallet-utils"

export default class extends Controller {
  static targets = ["payBtn", "status", "confirming", "confirmingText", "success", "explorerLink", "statusBadge"]
  static values = {
    createUrl: String,
    confirmUrl: String,
    statusUrl: String,
    ordersUrl: String,
    wallet: String,
    chain: { type: String, default: "solana:devnet" }
  }

  async pay() {
    try {
      this.payBtnTarget.disabled = true
      this.payBtnTarget.textContent = "Preparing..."

      // 1. Find wallet
      const { wallet, account } = await findWalletByAddress(this.walletValue)

      // 2. Create order and get payment params
      const csrfToken = getCsrfToken()
      const createResponse = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        }
      })

      if (!createResponse.ok) {
        const error = await createResponse.json()
        throw new Error(error.error || "Failed to create order")
      }

      const txParams = await createResponse.json()

      // 3. Build and sign transaction
      this.showConfirming("Sign the transaction in your wallet...")

      const txBytes = buildTransferTransaction({
        sender: txParams.sender,
        recipient: txParams.recipient,
        amountSol: txParams.amount_sol,
        blockhash: txParams.blockhash,
        lastValidBlockHeight: txParams.last_valid_block_height
      })

      const signature = await signAndSend({
        wallet, account,
        transaction: txBytes,
        chain: this.chainValue
      })

      // 4. Report signature to server
      this.updateConfirmingText("Confirming payment...")

      await fetch(this.confirmUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({
          order_id: txParams.order_id,
          signature
        })
      })

      // 5. Show success and poll status
      this.showSuccess(signature, txParams.order_id)

    } catch (error) {
      console.error("Checkout error:", error)
      const fullError = String(error)

      if (fullError.includes("User rejected") || fullError.includes("cancelled")) {
        this.showStatus("Transaction cancelled", "warning")
      } else if (fullError.includes("insufficient lamports")) {
        this.showStatus("Insufficient SOL balance to complete this payment", "error")
      } else {
        this.showStatus("Payment failed. Please try again.", "error")
      }
      this.resetPayBtn()
    }
  }

  showConfirming(text) {
    this.payBtnTarget.classList.add("hidden")
    this.confirmingTarget.classList.remove("hidden")
    this.confirmingTextTarget.textContent = text
  }

  updateConfirmingText(text) {
    this.confirmingTextTarget.textContent = text
  }

  showSuccess(signature, orderId) {
    this.confirmingTarget.classList.add("hidden")
    this.successTarget.classList.remove("hidden")

    const url = explorerUrl(signature, this.chainValue)
    this.explorerLinkTarget.href = url
    this.explorerLinkTarget.textContent = signature.slice(0, 8) + "..." + signature.slice(-4)

    this.statusBadgeTarget.textContent = "Submitted"
    this.statusBadgeTarget.className = "inline-block px-2 py-1 rounded-full text-xs bg-yellow-900/30 text-yellow-400"

    this.pollStatus(orderId)
  }

  async pollStatus(orderId) {
    const statusUrl = this.statusUrlValue.replace("ORDER_ID", orderId)

    for (let i = 0; i < 30; i++) {
      await new Promise(r => setTimeout(r, 2000))
      try {
        const res = await fetch(statusUrl)
        const data = await res.json()

        if (data.transfer_status === "finalized" || data.transfer_status === "confirmed") {
          this.statusBadgeTarget.textContent = "Confirmed"
          this.statusBadgeTarget.className = "inline-block px-2 py-1 rounded-full text-xs bg-green-900/30 text-green-400"

          setTimeout(() => {
            window.location.href = this.ordersUrlValue
          }, 2000)
          return
        }

        if (data.transfer_status === "failed") {
          this.statusBadgeTarget.textContent = "Failed"
          this.statusBadgeTarget.className = "inline-block px-2 py-1 rounded-full text-xs bg-red-900/30 text-red-400"
          return
        }
      } catch { /* continue polling */ }
    }
  }

  showStatus(message, type = "info") {
    const el = this.statusTarget
    el.textContent = message
    el.classList.remove("hidden", "bg-red-900/50", "text-red-300", "bg-yellow-900/50", "text-yellow-300", "bg-green-900/50", "text-green-300")
    switch (type) {
      case "error": el.classList.add("bg-red-900/50", "text-red-300"); break
      case "warning": el.classList.add("bg-yellow-900/50", "text-yellow-300"); break
      default: el.classList.add("bg-green-900/50", "text-green-300")
    }
    el.classList.remove("hidden")
  }

  resetPayBtn() {
    this.payBtnTarget.classList.remove("hidden")
    this.payBtnTarget.disabled = false
    this.payBtnTarget.textContent = "Pay with SOL"
    this.confirmingTarget.classList.add("hidden")
  }
}
